import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { DaghfalClient } from '../api/client';
import { ApiError, NetworkError } from '../api/problem';
import { ApiProvider } from '../auth/ApiProvider';
import { SessionProvider } from '../auth/SessionProvider';
import { I18nProvider } from '../i18n/I18nProvider';
import type { Catalogue } from '../i18n/types';
import { applyTheme, readStoredTheme } from '../ui/ThemeToggle';

export interface DaghfalAppProps {
  /** Namespaces this app's stored session and its query cache. One of the four app names. */
  appKey: string;
  /**
   * Which palette the brand square and primary colour come from: `parent`, `teacher` or `console`
   * (design/brand/tokens.json → app).
   */
  appearance: 'parent' | 'teacher' | 'console';
  /**
   * `platform` gives the shell its deliberately distinct colouring, so a platform operator is never in
   * doubt about which system they are in (FR-PLT-01).
   */
  surface?: 'tenant' | 'platform';
  catalogue?: Catalogue;
  children: ReactNode;
}

/**
 * The root of every Daghfal client: routing, the API client, the session, localisation and the query
 * cache, composed once here so the four applications differ only in their routes and their copy.
 */
export function DaghfalApp({ appKey, appearance, surface = 'tenant', catalogue, children }: DaghfalAppProps) {
  // Held in state so that React never sees a new client on a re-render; both are created exactly once.
  const [client] = useState(() => new DaghfalClient({
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5001',
    appKey,
  }));
  const [queryClient] = useState(createQueryClient);

  // The palette and surface are attributes on the root element rather than a wrapper class, so they also
  // reach anything rendered into a portal — a dialog's backdrop, for one.
  useEffect(() => {
    const root = globalThis.document?.documentElement;
    if (!root) return;
    root.dataset.app = appearance;
    root.dataset.surface = surface;
  }, [appearance, surface]);

  // A layout effect, so a device that pinned the theme opposite to its system never paints the wrong one first.
  useLayoutEffect(() => applyTheme(readStoredTheme()), []);

  const tree = useMemo(() => children, [children]);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider client={client}>
        <I18nProvider catalogue={catalogue}>
          <SessionProvider client={client}>
            <BrowserRouter>{tree}</BrowserRouter>
          </SessionProvider>
        </I18nProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // These clients run over 3G on low-end Android (SRS §2.3). A minute of staleness saves a great
        // many round trips on a screen a parent opens, closes and reopens.
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Retrying a refusal only wastes the connection: a 401 has already been through one token
          // refresh inside the client, and a 403 or 404 will not change on the next attempt.
          if (error instanceof ApiError) return false;
          // A dropped connection is worth retrying, because it often is not the server's doing.
          if (error instanceof NetworkError) return failureCount < 2;
          return failureCount < 1;
        },
      },
      mutations: {
        // A mutation is never retried automatically. Recording a meal or a payment twice because the
        // response was lost is worse than telling the user it failed and letting them decide.
        retry: false,
      },
    },
  });
}
